import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  Field,
  RegionSite,
  ReportRegion,
} from '@app/core/models/analytics.model';
import { ActiveSiteModel } from '@app/core/models/site.model';
import {
  AnalyticsActions,
  AnalyticsSelectors,
  SiteActions,
  SiteSelectors,
} from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { Subscription, combineLatest } from 'rxjs';

@Component({
  selector: 'app-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
})
export class DynamicFormComponent implements OnInit, OnDestroy {
  @Input() reportFilters: Field[];
  @Input() submitButtonText = 'Submit';
  @Output() onFormSubmit = new EventEmitter<FormGroup>();

  originalReportFilters: Field[];
  dynamicForm: FormGroup;
  maxDate: Date;
  loadingSites = false;
  loadingRegions = false;
  loadingRegionSites = false;
  subscriptions: Subscription[] = [];
  regionOptions: any[] = [];
  regionSitesOptions: any[] = [];
  locationOptions: any[] = [];

  constructor(
    private store: Store<AppState>,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    this.originalReportFilters = this.reportFilters;

    this.locationOptions =
      this.reportFilters?.find(field => field.name === 'location_account')
        ?.options || [];

    this.regionOptions =
      this.reportFilters?.find(field => field.name === 'region_id')?.options ||
      [];

    this.regionSitesOptions =
      this.reportFilters?.find(field => field.name === 'region_site')
        ?.options || [];

    this.maxDate = new Date();
    this.buildForm();
  }

  reset(): void {
    this.dynamicForm.get('customer_account').reset();
    const locationAccountControl = this.dynamicForm.get('location_account');
    const regionControl = this.dynamicForm.get('region_id');
    const regionSiteControl = this.dynamicForm.get('region_site');

    if (locationAccountControl) {
      locationAccountControl.reset();
      locationAccountControl.disable();
    }

    if (regionControl) {
      regionControl.reset();
      regionControl.disable();
      const regionIdField = this.getField('region_id');

      regionIdField.visible = false;
    }

    if (regionSiteControl) {
      regionSiteControl.reset();
      regionSiteControl.disable();
      const regionSiteField = this.getField('region_site');

      regionSiteField.visible = false;
    }
  }

  private buildForm(): void {
    this.ngOnDestroy();
    const formGroupConfig: { [key: string]: any } = {};

    for (const field of this.reportFilters) {
      formGroupConfig[field.name] =
        field.type === 'date-range'
          ? new FormGroup({
              begin_date: new FormControl(
                null,
                field.validators?.required ? Validators.required : null
              ),
              end_date: new FormControl(
                null,
                field.validators?.required ? Validators.required : null
              ),
            })
          : new FormControl(
              field.value || '',
              this.mapValidators(field.validators)
            );
    }

    this.dynamicForm = this.formBuilder.group(formGroupConfig);
    this.setupDynamicFormControls();
  }
  private setupDynamicFormControls(): void {
    const customerAccountControl = this.dynamicForm.get('customer_account');
    const locationAccountControl = this.dynamicForm.get('location_account');
    const regionControl = this.dynamicForm.get('region_id');
    const regionSiteControl = this.dynamicForm.get('region_site');

    const customerAccountField = this.getField('customer_account');
    const locationAccountField = this.getField('location_account');
    const regionIdField = this.getField('region_id');
    const regionSiteField = this.getField('region_site');

    // Initially disable location_account and region_id
    if (locationAccountControl) locationAccountControl.disable();
    if (regionControl) regionControl.disable();
    if (regionSiteControl) regionSiteControl.disable();

    if (customerAccountControl && customerAccountField?.options) {
      this.subscriptions.push(
        customerAccountControl.valueChanges.subscribe(value => {
          this.resetControls([
            locationAccountControl,
            regionControl,
            regionSiteControl,
          ]);
          const selectedCustomerAccount = customerAccountField?.options.find(
            option => option.value === value
          );

          if (selectedCustomerAccount) {
            if (locationAccountControl) locationAccountControl.enable();
            if (regionControl) regionControl.enable();

            this.handleCustomerAccountSelection(
              selectedCustomerAccount,
              locationAccountField,
              locationAccountControl,
              regionIdField,
              regionControl
            );
          }
        })
      );
    }

    if (regionControl) {
      if (regionSiteControl) {
        this.subscriptions.push(
          regionControl.valueChanges.subscribe(value => {
            regionSiteControl.setValue('');

            if (value) {
              regionSiteControl.enable();
            } else {
              regionSiteControl.disable();
            }

            const selectedRegion = regionIdField?.options.find(
              option => option.value === value
            );

            if (selectedRegion) {
              const selectedCustomerAccount =
                customerAccountField?.options.find(
                  option => option.value === customerAccountField.value
                );

              this.getRegionSites(selectedCustomerAccount, selectedRegion);
            }
          })
        );
      }
    }

    this.setupStoreSubscriptions(
      customerAccountField,
      locationAccountField,
      locationAccountControl,
      regionIdField,
      regionControl,
      regionSiteField,
      regionSiteControl
    );
  }

  private setupStoreSubscriptions(
    customerAccountField: Field,
    locationAccountField: Field,
    locationAccountControl: AbstractControl,
    regionIdField: Field,
    regionControl: AbstractControl,
    regionSiteField: Field,
    regionSiteControl: AbstractControl
  ): void {
    if (locationAccountField) {
      this.subscriptions.push(
        this.store
          .select(SiteSelectors.getAllActiveSitesForSelectedBillinAccs)
          .subscribe((allActiveSites: ActiveSiteModel[]) => {
            this.updateLocationOptions(
              locationAccountField,
              locationAccountControl,
              allActiveSites
            );
          })
      );
    }

    if (regionIdField) {
      this.subscriptions.push(
        this.store
          .select(AnalyticsSelectors.getReportRegionList)
          .subscribe((reportRegionList: ReportRegion[]) => {
            if (customerAccountField.value) {
              if (reportRegionList.length === 0) {
                if (regionControl) {
                  regionControl.setValidators(null);
                  regionIdField.visible = false;
                  regionControl.updateValueAndValidity();
                }

                if (regionSiteControl) {
                  regionSiteControl.setValidators(null);
                  regionSiteField.visible = false;
                  regionSiteControl.updateValueAndValidity();
                }

                if (locationAccountControl) {
                  locationAccountControl.setValidators([Validators.required]);
                  locationAccountField.visible = true;
                  locationAccountControl.updateValueAndValidity();
                }
              } else {
                if (regionControl) {
                  regionControl.setValidators([Validators.required]);
                  regionIdField.visible = true;
                  regionControl.updateValueAndValidity();
                }

                if (regionSiteControl) {
                  regionSiteControl.setValidators([Validators.required]);
                  regionSiteField.visible = true;
                  regionSiteControl.updateValueAndValidity();
                }

                if (locationAccountControl) {
                  locationAccountControl.setValidators(null);
                  locationAccountField.visible = false;
                  locationAccountControl.updateValueAndValidity();
                }

                this.updateRegionOptions(
                  regionIdField,
                  regionControl,
                  reportRegionList
                );
              }
            }

            this.loadingRegions = false;
          })
      );
    }

    if (regionSiteField) {
      this.subscriptions.push(
        combineLatest([
          this.store.select(AnalyticsSelectors.getRegionSiteList),
          this.store.select(
            SiteSelectors.getAllActiveSitesForSelectedBillinAccs
          ),
        ]).subscribe(
          ([regionSiteList, allActiveSites]: [
            RegionSite[],
            ActiveSiteModel[],
          ]) => {
            this.updateRegionSiteOptions(
              regionSiteField,
              regionSiteControl,
              regionSiteList,
              allActiveSites
            );
          }
        )
      );
    }
  }

  private resetControls(controls: (AbstractControl | null)[]): void {
    controls.forEach(control =>
      control ? control && control.setValue('') : null
    );
  }

  private handleCustomerAccountSelection(
    selectedCustomerAccount: any,
    locationAccountField: Field,
    locationAccountControl: AbstractControl,
    regionIdField: Field,
    regionControl: AbstractControl
  ): void {
    if (locationAccountField) {
      if (selectedCustomerAccount.sites.length) {
        this.updateLocationOptions(
          locationAccountField,
          locationAccountControl,
          selectedCustomerAccount.sites
        );
      } else {
        if (locationAccountControl) locationAccountControl.disable();
        if (regionControl) regionControl.disable();
        this.loadingSites = true;

        this.store.dispatch(
          SiteActions.getActiveSites({
            billingAccountIds: [selectedCustomerAccount.id],
          })
        );
      }
    }

    if (regionIdField) {
      this.loadingRegions = true;

      this.store.dispatch(
        AnalyticsActions.getReportRegionByBillingAccount({
          requestBody: {
            billingAccountGuid: selectedCustomerAccount.id?.toUpperCase(),
          },
        })
      );
    }
  }

  private getRegionSites(
    selectedCustomerAccount: any,
    selectedRegion: any
  ): void {
    this.loadingSites = true;
    this.loadingRegionSites = true;

    this.store.dispatch(
      SiteActions.getActiveSites({
        billingAccountIds: [selectedCustomerAccount.id],
      })
    );

    this.store.dispatch(
      AnalyticsActions.getSitesByRegion({
        requestBody: { regionId: Number(selectedRegion.value) },
      })
    );
  }

  private updateLocationOptions(
    locationAccountField: Field,
    locationAccountControl: AbstractControl,
    sites: ActiveSiteModel[]
  ): void {
    if (sites.length) {
      const options = sites.map(site => ({
        value: site.accountNumber,
        label: site.name,
      }));

      locationAccountField.options = [...this.locationOptions, ...options];
      locationAccountControl.enable();
    }

    this.loadingSites = false;
  }

  private updateRegionOptions(
    regionIdField: Field,
    regionControl: AbstractControl,
    reportRegionList: ReportRegion[]
  ): void {
    if (reportRegionList.length) {
      const options = reportRegionList.map(region => ({
        value: region.regionId,
        label: region.regionName,
      }));

      regionIdField.options = [...this.regionOptions, ...options];
      regionControl.enable();
    }
  }

  private updateRegionSiteOptions(
    regionSiteField: Field,
    regionSiteControl: AbstractControl,
    regionSiteList: RegionSite[],
    allActiveSites: ActiveSiteModel[]
  ): void {
    if (regionSiteList.length) {
      let regionSites = regionSiteList.map(regionSite => ({
        value: regionSite.ceAccountId.toLowerCase(),
        label: 'N/A',
      }));

      if (allActiveSites.length) {
        regionSites = regionSites.map(option => {
          const site = allActiveSites.find(site => site.id === option.value);

          return site
            ? { value: site.accountNumber, label: site.name }
            : option;
        });
      }

      regionSiteField.options = regionSites;
      regionSiteField.options = [...this.regionSitesOptions, ...regionSites];
      regionSiteControl.enable();
    }
    this.loadingSites = false;
    this.loadingRegionSites = false;
  }

  private mapValidators(validators: { [key: string]: any } | null): any[] {
    const formValidators = [];

    if (validators) {
      for (const validation of Object.keys(validators)) {
        if (validation === 'required' && validators[validation]) {
          formValidators.push(Validators.required);
        }
        // Add more validators if needed
      }
    }
    return formValidators;
  }

  onSubmit(): void {
    this.onFormSubmit.emit(this.dynamicForm);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  private getField(fieldName: string): Field | undefined {
    return this.reportFilters.find(field => field.name === fieldName);
  }
}
